import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { CadastroService } from '../../../../../services/cadastro.service';
import { FuncionarioCargoProducao, VendedorCadastro } from '../../../../../models/cadastro.model';
import { ErrorModel } from '../../../../../models/error.model';
import { FuncionarioModal } from '../../../../modal/funcionario-modal/funcionario-modal';

@Component({
  selector: 'app-vendedores-manutencao',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FuncionarioModal],
  templateUrl: './vendedores-manutencao.component.html',
  styleUrls: ['./vendedores-manutencao.component.css']
})
export class VendedoresManutencaoComponent implements OnInit {
  vendedorForm: FormGroup;
  vendedorId = 0;
  isEditMode = false;
  isLoading = false;
  isFuncionarioModalOpen = false;

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private cadastroService = inject(CadastroService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.vendedorForm = this.fb.group({
      idFuncionario: [null, [Validators.required, Validators.min(1)]],
      nomeFuncionario: ['', [Validators.required]],
      sobrenomeFuncionario: [''],
      ativo: [true]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.vendedorId = +params['id'];
        this.isEditMode = true;
        this.loadVendedor(this.vendedorId);
      }
    });
  }

  get nomeFuncionarioCompleto(): string {
    const nome = this.vendedorForm.get('nomeFuncionario')?.value ?? '';
    const sobrenome = this.vendedorForm.get('sobrenomeFuncionario')?.value ?? '';
    return `${nome} ${sobrenome}`.trim();
  }

  get funcionarioInvalido(): boolean {
    const control = this.vendedorForm.get('idFuncionario');
    return !!control && control.invalid && control.touched;
  }

  loadVendedor(id: number): void {
    this.isLoading = true;
    this.cadastroService.getVendedorById(id).subscribe({
      next: (vendedor) => {
        this.vendedorForm.patchValue(vendedor);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao carregar vendedor.');
        this.isLoading = false;
        this.router.navigate(['/home', 'cadastro-vendedor']);
      }
    });
  }

  abrirFuncionarioModal(): void {
    this.isFuncionarioModalOpen = true;
    this.cdr.detectChanges();
  }

  closeFuncionarioModal(funcionario?: FuncionarioCargoProducao): void {
    this.isFuncionarioModalOpen = false;

    if (!funcionario) {
      this.vendedorForm.get('idFuncionario')?.markAsTouched();
      this.cdr.detectChanges();
      return;
    }

    // Busca o cadastro completo para obter nome e sobrenome separados,
    // como o payload de vendedores exige.
    this.cadastroService.getFuncionarioById(funcionario.idFuncionario).subscribe({
      next: (func) => {
        this.vendedorForm.patchValue({
          idFuncionario: func.id,
          nomeFuncionario: func.nome,
          sobrenomeFuncionario: func.sobrenome
        });
        this.vendedorForm.get('idFuncionario')?.markAsTouched();
        this.cdr.detectChanges();
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao carregar dados do funcionário.');
        this.cdr.detectChanges();
      }
    });
  }

  salvar(): void {
    if (!this.vendedorForm.valid) {
      this.vendedorForm.markAllAsTouched();
      this.toastr.warning('Selecione o funcionário antes de salvar.');
      return;
    }

    this.isLoading = true;
    const vendedor: VendedorCadastro = {
      ...this.vendedorForm.value,
      id: this.isEditMode ? this.vendedorId : 0
    };

    const operation = this.isEditMode
      ? this.cadastroService.updateVendedor(vendedor)
      : this.cadastroService.createVendedor(vendedor);

    operation.subscribe({
      next: () => {
        const mensagem = this.isEditMode ? 'Vendedor atualizado com sucesso!' : 'Vendedor criado com sucesso!';
        this.toastr.success(mensagem);
        this.router.navigate(['/home', 'cadastro-vendedor']);
        this.isLoading = false;
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao salvar vendedor.');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  cancelar(): void {
    this.router.navigate(['/home', 'cadastro-vendedor']);
  }
}
